import * as React from 'react';

import { cn } from '@/lib/utils';

function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    // SL-0027, Activity 4, Task 4.4: the mockup's own `.table-wrap` (border/radius/background,
    // shared with `.card`/`.panel`) applied to the scroll container.
    <div
      data-slot="table-container"
      className="bg-card border-border relative w-full overflow-x-auto rounded-md border"
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'border-b transition-colors hover:bg-muted/50 has-aria-expanded:bg-muted/50 data-[state=selected]:bg-muted',
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        // SL-0027, Activity 4, Task 4.4: the mockup's `th, td { padding: 13px 18px; border-top: 1px
        // solid var(--border); text-align: left; vertical-align: top; }` applies to BOTH cell
        // types jointly (confirmed at execution time) — `align-top`/`border-t` are ported to `th`
        // here too, even though the task's own `th` bullet named only the `th`-specific properties.
        // The vertical padding is now the density token (proposal decision 7): 13px comfortable,
        // 7px compact. The header row takes the padding but NOT the row height — 52px of header
        // above a compact table is exactly the space compact exists to reclaim.
        'border-border text-muted-foreground border-t px-[18px] py-[var(--density-cell-y)] text-left align-top text-[12px] font-bold tracking-[0.04em] whitespace-nowrap uppercase [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        // `h-[…]` on a `<td>` is a MINIMUM in table layout, not a fixed height, so a two-line cell
        // still grows — and the padding has to come down with the row height or compact would
        // change nothing at all, since 13+18+13 already exceeds 40px on its own.
        'border-border border-t px-[18px] py-[var(--density-cell-y)] h-[var(--density-row-h)] text-left align-top text-[14px] leading-[1.35] whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
